import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./Login.css";
import useTranslation from '../hooks/useTranslation';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';

const GlassInputWrapper = ({ children }) => (
  <div className="glass-input-wrapper">
    {children}
  </div>
);

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!email || !token) {
      setMessage('Invalid reset link. Please request a new password reset.');
    }
  }, [email, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!newPassword || !confirmPassword) {
      setMessage('Please enter both passwords.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      const base = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:8000' : 'https://app.neurolonic.com/api');
      
      const setResponse = await fetch(`${base}/mobile/reset-password/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reset_token: token, new_password: newPassword })
      });

      const setData = await setResponse.json();

      if (setResponse.ok) {
        setSuccess(true);
        setMessage('Password reset successfully! Redirecting to login...');
        setTimeout(() => navigate('/'), 3000);
      } else {
        setMessage(setData.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <h1 className="reset-password-title">{t('Reset Your Password')}</h1>
          <p className="reset-password-subtitle">
            {t('Enter your new password below')}
          </p>

          {message && (
            <div className={`reset-password-message ${success ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          {!success && email && token && (
            <form className="reset-password-form" onSubmit={handleSubmit}>
              <div className="form-group-modal">
                <label className="form-label-modal">{t('New Password')}</label>
                <GlassInputWrapper>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('Enter new password')}
                      className="form-input-modal password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? 
                        <AiOutlineEyeInvisible className="eye-icon" /> : 
                        <AiOutlineEye className="eye-icon" />
                      }
                    </button>
                  </div>
                </GlassInputWrapper>
              </div>

              <div className="form-group-modal">
                <label className="form-label-modal">{t('Confirm Password')}</label>
                <GlassInputWrapper>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('Confirm new password')}
                    className="form-input-modal"
                    required
                  />
                </GlassInputWrapper>
              </div>

              <button 
                type="submit" 
                className="reset-password-btn"
                disabled={loading}
              >
                {loading ? t('Resetting...') : t('Reset Password')}
              </button>
            </form>
          )}

          <div className="reset-password-login">
            <button 
              type="button" 
              className="login-signup-link"
              onClick={() => navigate('/')}
            >
              {t('Back to Login')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
